import { useEffect, useState } from 'react';

import { AttemptActions } from './AttemptActions';
import { ChoosePathDialog } from './ChoosePathDialog';
import { openEmbeddedSignup } from './meta-embedded-signup';
import { describeAttempt, EM_ANDAMENTO, stepsFor } from './onboarding-diagnosis';
import { OnboardingSteps } from './OnboardingSteps';
import { useCancelAttempt, usePostPopupResult, useStartAttempt } from './use-onboarding';

import type { ChoosePathResult } from './ChoosePathDialog';
import type { Tone } from './onboarding-diagnosis';
import type { OnboardingAttemptWire, OnboardingAvailabilityWire } from './types';
import type { ReactElement } from 'react';

const BORDA: Record<Tone, string> = {
  ok: 'border-l-emerald-600',
  bad: 'border-l-red-600',
  warn: 'border-l-amber-600',
  mute: 'border-l-border-subtle',
  busy: 'border-l-accent',
};
const PILULA: Record<Tone, string> = {
  ok: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  bad: 'bg-red-50 border-red-200 text-red-700',
  warn: 'bg-amber-50 border-amber-200 text-amber-700',
  mute: 'bg-bg-muted border-border-default text-text-secondary',
  busy: 'bg-amber-100 border-amber-200 text-amber-900',
};
const CAMINHO: Record<OnboardingAttemptWire['path'], string> = {
  dedicated: 'número novo ou dedicado',
  business_app: 'o número que uso no app do celular',
  migration: 'trazer de outro sistema',
};

interface PopupCfg {
  appId: string;
  configId: string | null;
  graphVersion: string;
  featureType: string | null;
}

export interface WhatsAppConnectCardProps {
  canManage: boolean;
  /** A tentativa corrente do tenant (ou null). Quem busca é a aba, para o ChannelCard também ler. */
  attempt: OnboardingAttemptWire | null;
  availability: OnboardingAvailabilityWire | undefined;
  /** Vindo de «Trocar de número»: abre a escolha do caminho sem novo clique (revisão de 30/Set, menor 7). */
  abrirEscolha?: boolean;
  /** Chamado quando a escolha abriu por `abrirEscolha` — a aba baixa o pedido. */
  onEscolhaAberta?: () => void;
}

/**
 * O cartão do WhatsApp ANTES de existir canal (pranchas A1, A3, A4, A5, A6). A partir do passo
 * 1 do pipeline o canal existe e o `ChannelCard` assume, recebendo a mesma tentativa — e o
 * mesmo `AttemptActions`, para o gesto do catálogo existir nos dois lugares.
 *
 * O `code` do popup vale 30 s: `abrirPopup` posta o resultado no instante em que a promessa
 * resolve, sem passar por estado React.
 */
export function WhatsAppConnectCard({
  canManage,
  attempt,
  availability,
  abrirEscolha = false,
  onEscolhaAberta,
}: WhatsAppConnectCardProps): ReactElement {
  const [escolhendo, setEscolhendo] = useState(false);
  useEffect(() => {
    if (!abrirEscolha) return;
    setEscolhendo(true);
    onEscolhaAberta?.();
  }, [abrirEscolha, onEscolhaAberta]);
  const [erroLocal, setErroLocal] = useState<string | null>(null);
  const start = useStartAttempt();
  const postar = usePostPopupResult();
  const cancelar = useCancelAttempt();

  async function abrirPopup(id: string, popup: PopupCfg): Promise<void> {
    if (popup.configId === null) {
      setErroLocal(
        'A Lume ainda não terminou a configuração na Meta (falta o identificador da tela de conexão).',
      );
      return;
    }
    try {
      const r = await openEmbeddedSignup({
        appId: popup.appId,
        configId: popup.configId,
        graphVersion: popup.graphVersion,
        featureType: popup.featureType,
      });
      postar.mutate({ id, body: r });
    } catch (e) {
      setErroLocal(e instanceof Error ? e.message : 'não foi possível abrir a janela da Meta');
    }
  }

  function conectar(r: ChoosePathResult): void {
    setErroLocal(null);
    start.mutate(r, {
      onSuccess: (s) => {
        setEscolhendo(false);
        void abrirPopup(s.attempt.id, s.popup);
      },
    });
  }

  const erros = [
    erroLocal,
    start.isError ? start.error.message : null,
    postar.isError ? postar.error.message : null,
    cancelar.isError ? cancelar.error.message : null,
  ].filter((e): e is string => e !== null);

  // ── A1: nada em andamento ──────────────────────────────────────────────────
  const terminal =
    attempt === null || attempt.status === 'canceled' || attempt.status === 'connected';
  if (terminal) {
    const pronta = availability?.ready === true;
    return (
      <div
        className={`mb-3 rounded-lg border border-border-default border-l-[3px] bg-surface p-4 ${BORDA.mute}`}
      >
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-text-primary">WhatsApp</span>
              <span className={`rounded-full border px-2 py-0.5 text-xs ${PILULA.mute}`}>
                Não conectado
              </span>
            </div>
            <p className="mt-1.5 max-w-xl text-xs leading-relaxed text-text-secondary">
              Seu número de WhatsApp ainda não está ligado à Lume. Leva alguns minutos, acontece
              aqui dentro, e você <strong>não precisa abrir o painel da Meta</strong>. Serve para
              número novo, para o número que você já usa no celular, ou para trazer um número de
              outro sistema.
            </p>
            {availability !== undefined && !pronta && (
              <p className="mt-2 text-xs text-amber-700">
                A Lume ainda não terminou a configuração na Meta para esta conta. Assim que
                terminar, o botão aparece aqui.
              </p>
            )}
            {erros.map((e) => (
              <p key={e} className="mt-2 text-xs text-red-700" role="alert">
                Não deu: {e}
              </p>
            ))}
          </div>
          {canManage && pronta && (
            <button
              type="button"
              className="rounded-md bg-accent px-3.5 py-2 text-xs font-medium text-white hover:bg-accent-hover"
              onClick={() => {
                setEscolhendo(true);
              }}
            >
              Conectar WhatsApp
            </button>
          )}
        </div>
        {escolhendo && (
          <ChoosePathDialog
            pending={start.isPending}
            onClose={() => {
              setEscolhendo(false);
            }}
            onConfirm={conectar}
          />
        )}
      </div>
    );
  }

  // ── A3/A4/A5/A6: há tentativa ──────────────────────────────────────────────
  const d = describeAttempt(attempt);
  const mostrarPassos =
    attempt.stepsDone.length > 0 ||
    EM_ANDAMENTO.includes(attempt.status) ||
    attempt.status === 'failed';
  const popupAberto = attempt.status === 'opened' && attempt.metaEvent === null;
  return (
    <div
      className={`mb-3 rounded-lg border border-border-default border-l-[3px] bg-surface p-4 ${BORDA[d.tone]}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-text-primary">WhatsApp</span>
            <span className={`rounded-full border px-2 py-0.5 text-xs ${PILULA[d.tone]}`}>
              {d.label}
            </span>
          </div>
          <div className="mt-0.5 font-mono text-xs text-text-secondary">
            {CAMINHO[attempt.path]}
          </div>
        </div>
        {canManage && (popupAberto || attempt.metaEvent === 'CANCEL') && (
          <button
            type="button"
            className="rounded-md border border-border-strong bg-surface px-2.5 py-1 text-xs text-text-secondary disabled:opacity-50"
            disabled={cancelar.isPending}
            onClick={() => {
              cancelar.mutate(attempt.id);
            }}
          >
            Cancelar
          </button>
        )}
      </div>

      {mostrarPassos && <OnboardingSteps steps={stepsFor(attempt)} />}

      <AttemptActions
        attempt={attempt}
        canManage={canManage}
        onEscolherOutroCaminho={() => {
          setEscolhendo(true);
        }}
      />

      {erros.map((e) => (
        <p key={e} className="mt-2 text-xs text-red-700" role="alert">
          Não deu: {e}
        </p>
      ))}

      {escolhendo && (
        <ChoosePathDialog
          pending={start.isPending}
          onClose={() => {
            setEscolhendo(false);
          }}
          onConfirm={conectar}
        />
      )}
    </div>
  );
}
